import React, { FC, useEffect, useState } from 'react';

import { Box, Icon } from '@rbilabs/universal-components';
import { useLocalSearchParams } from 'expo-router';
import { useRouter } from 'expo-router';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';

import { IBackendCartEntries, ILegacyCartEntry } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import ConfirmDialog from 'components/confirm-dialog';
import { FavoriteInput } from 'components/favorite-input';
import ItemActions from 'components/item-actions';
import { Product } from 'components/product';
import { SaveFavoriteOrderUnsaved } from 'components/save-favorite-order-unsaved';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import {
  GetUserFavoritesDocument,
  ICartEntryInput,
  useDeleteFavoriteMutation,
  useGetUserFavoriteQuery,
  useUpdateFavoriteMutation,
} from 'generated/graphql-gateway';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import useFoodware from 'hooks/use-foodware';
import { isCartEntryReorderable } from 'hooks/use-reorderable-orders/utils';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useFavoritesContext } from 'state/favorites';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { getDesignToken, primitive } from 'styles/constants';
import { frontendToBackendCartEntryMap, getSelectionsListFromEntry } from 'utils/cart';
import { mapBackendToFrontend } from 'utils/reorder';
import { routes } from 'utils/routing';

const StyledCard = Box.withConfig({
  padding: '$4',
  backgroundColor: getDesignToken('background-pattern'),
  borderBottomWidth: '1px',
  borderBottomColor: primitive.$greyFour,
});

const StyledItemActions = Box.withConfig({
  marginTop: '$2',
  marginLeft: '$0',
  marginBottom: '$0',
  marginRight: '$16',
});

const FavoriteEditorPage: FC<React.PropsWithChildren<unknown>> = () => {
  useRumPageView('favorite-editor', 'Favorite Editor');

  const {
    setCurrentEditingFavorite,
    cancelEditing,
    tempFavorite: favorite,
    isPendingSave,
    editName,
    editEntry,
    removeEntry,
    setShouldRefetchFavorites,
  } = useFavoritesContext();

  const router = useRouter();

  const { favoriteId } = useLocalSearchParams<{ favoriteId: string }>();
  const [unSavedChangesModalOpen, setUnSavedChangesModalOpen] = useState<boolean>(false);
  const [removeFavoriteDialog, setRemoveFavoriteDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  const [deleteFavorite] = useDeleteFavoriteMutation({
    onCompleted: () => {
      toaster.positive(t('removeFavoriteOrderSucess'));

      setShouldRefetchFavorites(true);
      router.dismiss();
    },
  });

  const [updateFavorite] = useUpdateFavoriteMutation({
    onCompleted: () => {
      cancelEditing();
      setShouldRefetchFavorites(true);
      router.dismissTo(routes.favorites);
    },
  });

  const { loading } = useGetUserFavoriteQuery({
    variables: {
      favoriteId,
    },
    onCompleted: data => setCurrentEditingFavorite(data?.userFavorite),
  });

  const handleCloseEditor = () => {
    if (isPendingSave) {
      return setUnSavedChangesModalOpen(true);
    }
    cancelEditing();
    router.dismiss();
  };

  const handleSaveEdits = async () => {
    setErrorMessage('');
    logEvent(CustomEventNames.EDIT_FAVORITE, EventTypes.UserContent, {
      'Favorite Id': favoriteId,
    });

    if (!favorite?.name || favorite?.name.trim() === '') {
      return setErrorMessage(t('saveFavoriteOrderNameError'));
    }
    // Cast favorite entries to backend entries
    const cartEntries = (favorite?.entries as unknown) as IBackendCartEntries[];
    // Convert backend entries to cart entry input
    const entries = (cartEntries?.map(cartEntry =>
      frontendToBackendCartEntryMap(mapBackendToFrontend(cartEntry))
    ) as unknown) as ICartEntryInput[];
    // Update the favorite
    await updateFavorite({
      variables: {
        favoriteId,
        input: {
          ref: favorite?.ref,
          entries,
          name: favorite?.name.trim(),
        },
      },
    });
    cancelEditing();
  };

  // @TODO: Figure out how to infer the type from the favoritesFragment
  const handleEditItem = (entry: any) => {
    /**
     * Redirects to wizard
     *
     * If you click Save Changes in wizard it will:
     *  - Set `isPendingSave` to true
     *  - then will update the tempFavorite
     */
    editEntry(entry);
  };

  const handleRemoveItem = (lineId?: string) => {
    // Not sure when this would ever happen? Here for type safety
    if (!lineId) {
      return;
    }

    // If there is one favorite item remaining, then ask the user to remove the current favorite
    if (favorite?.entries && favorite.entries.length <= 1) {
      return setRemoveFavoriteDialog(true);
    }

    /**
     * Removes a line item from favorite order given the lineId
     *
     *  - Sets `isPendingSave` to true
     *  - then will update the tempFavorite
     */
    removeEntry(lineId);
  };

  const removeFavorite = () => {
    deleteFavorite({
      variables: {
        favoriteId,
      },
      refetchQueries: [{ query: GetUserFavoritesDocument }],
    });
    setRemoveFavoriteDialog(false);
  };

  const discardChanges = () => {
    router.replace(routes.favorites);
    cancelEditing();
  };

  const favoriteEntries = ((favorite?.entries as unknown) as
    | IBackendCartEntries[]
    | null
    | undefined)?.reduce<React.JSX.Element[]>((acc, favoriteCartEntry, currentIndex) => {
    if (!isCartEntryReorderable(favoriteCartEntry, sanityIdsExcludedFromReordering, isFoodware)) {
      return acc;
    }

    acc.push(
      <FavoriteEntry
        key={'favEntry' + currentIndex}
        entry={favoriteCartEntry}
        handleEditItem={handleEditItem}
        handleRemoveItem={handleRemoveItem}
      />
    );

    return acc;
  }, []);

  return (
    <CardModal
      testID="favorite-editor"
      headerText={t('editFavorite')}
      onDismiss={handleCloseEditor}
      scrollView
      footer={
        <ActionButton fullWidth onPress={handleSaveEdits}>
          {t('update')}
        </ActionButton>
      }
    >
      {loading ? (
        <LoyaltyLoader />
      ) : (
        <>
          <Box margin="$4">
            <FavoriteInput
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              onChange={editName}
              testID="favorite-editor-name-input"
              favoriteName={favorite?.name || ''}
            />
          </Box>
          {favoriteEntries}
          <Box margin="$4">
            <ActionButton
              onPress={() => router.replace(routes.menu)}
              linkPath={routes.menu}
              startIcon={<Icon variant="add" aria-hidden />}
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              fullWidth
            >
              {t('addItems')}
            </ActionButton>
          </Box>
        </>
      )}
      {unSavedChangesModalOpen && (
        <SaveFavoriteOrderUnsaved
          onSave={handleSaveEdits}
          onDiscard={discardChanges}
          onDismiss={() => setUnSavedChangesModalOpen(false)}
        />
      )}
      {removeFavoriteDialog && (
        <ConfirmDialog
          showDialog
          testID="remove-favorite-order-modal"
          heading={t('favoriteRemoveAllModalHeader')}
          body={t('favoriteRemoveAllModalBody')}
          cancelLabel={t('cancel')}
          confirmLabel={t('deleteButtonText')}
          onCancel={() => setRemoveFavoriteDialog(false)}
          onConfirm={removeFavorite}
          modalAppearanceEventMessage="Remove Favorite"
        />
      )}
    </CardModal>
  );
};

interface IFavoriteEntry {
  entry: IBackendCartEntries;
  handleEditItem: (entry: any) => void;
  handleRemoveItem: (lineId?: string) => void;
}

const FavoriteEntry = ({ entry, handleEditItem, handleRemoveItem }: IFavoriteEntry) => {
  const [itemAvailability, setItemAvailability] = useState<ItemAvailabilityStatus>(
    ItemAvailabilityStatus.UNAVAILABLE
  );

  const mapToFrontendEntry = useMapToFrontendCartEntry();

  useEffect(() => {
    let isSubscribed = true;

    const handleEffect = async () => {
      const { status } = await mapToFrontendEntry(entry);
      if (isSubscribed) {
        setItemAvailability(status);
      }
    };

    handleEffect();

    return () => {
      isSubscribed = false;
    };
  }, [entry, mapToFrontendEntry]);

  return (
    <StyledCard key={entry?.lineId}>
      <Product
        quantity={entry?.quantity}
        name={entry?.name}
        image={entry?.image}
        selections={getSelectionsListFromEntry((entry as unknown) as ILegacyCartEntry)}
        quantityBadgeTextStyle={{
          color: primitive.$mayoEggWhite,
        }}
        quantityBadgeStyle={{
          top: '-10px',
          height: '20px',
          width: '20px',
          justifyContent: 'center',
        }}
      />
      <StyledItemActions>
        <ItemActions
          showEdit={itemAvailability === ItemAvailabilityStatus.AVAILABLE}
          onEdit={() => handleEditItem(entry)}
          onRemove={() => handleRemoveItem(entry?.lineId)}
        />
      </StyledItemActions>
    </StyledCard>
  );
};

export default withMenuEmptyState(FavoriteEditorPage);
